import { baseApi } from "./baseApi";
import type { Role, RoleDefinition } from "../../../shared/roles";
import { getUsersApi } from "../platform/platformService";
import type { ICurrentUser } from "../../views/settings/security/security.interface";

// Types for RBAC API
export interface RoleWithOverrides extends RoleDefinition {
  hasOverrides: boolean;
}

export interface MyPermissions {
  role: Role | string;
  roleLabel: string;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  isB2CAdmin?: boolean;
  isAdmin?: boolean;
  isManager?: boolean;
  isUserPortal?: boolean;
  hasNoAppRole?: boolean;
  canAccessSettings?: boolean;
  agentId?: string;
  source?: "b2c_admin" | "global" | "tenant" | "instance";
  hiddenEmails?: string[];
  skyPointAdminEmails?: string[];
  skyPointAdminUsers?: Array<{ email: string; displayName: string }>;
}

// Role Assignment types (new RBAC system)
export interface RoleAssignment {
  id: string;
  userId: number;
  roleId: string;
  tenantId: string | null;
  instanceId: string | null;
  assignedBy: number | null;
  assignedAt: string;
}

export interface CreateRoleAssignmentRequest {
  userId: number;
  roleId: string;
  tenantId?: string | null;
  instanceId?: string | null;
}

export interface UpdateRoleAssignmentRequest {
  roleId?: string;
  tenantId?: string | null;
  instanceId?: string | null;
}

export const rbacApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Current User Permissions
    getMyPermissions: builder.query<MyPermissions, void>({
      query: () => "/admin/rbac/my-permissions",
      providesTags: [{ type: "RBAC", id: "MY_PERMISSIONS" }],
      // Never cache — user's role can change at any time
      keepUnusedDataFor: 0,
    }),

    // Role Assignment CRUD (new RBAC system)
    getAssignments: builder.query<
      RoleAssignment[],
      { userId?: number; instanceId?: string; tenantId?: string } | void
    >({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters) {
          if (filters.userId) params.set("userId", String(filters.userId));
          if (filters.instanceId) params.set("instanceId", filters.instanceId);
          if (filters.tenantId) params.set("tenantId", filters.tenantId);
        }
        const qs = params.toString();
        return `/admin/rbac/assignments${qs ? `?${qs}` : ""}`;
      },
      providesTags: [{ type: "RBAC", id: "ASSIGNMENTS" }],
    }),

    createAssignment: builder.mutation<RoleAssignment, CreateRoleAssignmentRequest>({
      query: (body) => ({
        url: "/admin/rbac/assignments",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ASSIGNMENTS" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
      ],
    }),

    updateAssignment: builder.mutation<
      RoleAssignment,
      { id: string } & UpdateRoleAssignmentRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/rbac/assignments/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ASSIGNMENTS" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
      ],
    }),

    deleteAssignment: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/admin/rbac/assignments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ASSIGNMENTS" },
        { type: "RBAC", id: "MY_PERMISSIONS" },
      ],
    }),

    // Platform Users (for merging with RBAC data)
    getPlatformUsers: builder.query<{ users: ICurrentUser[] }, void>({
      queryFn: async () => {
        try {
          const response = await getUsersApi();
          return { data: response };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
      providesTags: [{ type: "RBAC", id: "PLATFORM_USERS" }],
    }),
  }),
});

export const {
  useGetMyPermissionsQuery,
  useGetAssignmentsQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  useGetPlatformUsersQuery,
} = rbacApi;
